import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { InfraAPI } from "@/lib/infra-api";
import { SlideOver, SaveStatus } from "./SlideOver";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ChevronDown, ChevronRight, Search, CheckCircle2, Circle, Trash2, Loader2, Upload, ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/**
 * Generic facility grid panel used by every "list of togglable configurable
 * things" card: academic / sports / student facilities, smart campus items,
 * safety items, utilities, and asset categories.
 *
 * Props:
 *   resource — backend collection name (facilities | smart | safety | utilities | assets)
 *   category — only used when `resource === 'facilities'` (academic|sports|student)
 *   catalog  — array of { kind, label, fields[] } to render
 *
 * Pattern: each catalog entry appears as a row. Toggle on the right enables
 * it — on enable we POST a placeholder doc, on disable we DELETE (soft).
 * Once enabled, clicking the row expands to reveal an auto-generated form
 * whose fields save on blur into the `config` JSON blob.
 */
export function FacilitiesPanel({ open, onClose, campusId, resource = "facilities", category, catalog, title, subtitle, testidKey }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState("idle");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(new Set());

  const queryKey = ["infra", resource, campusId, category || null];
  const listQ = useQuery({
    queryKey,
    queryFn: () =>
      api
        .get(`/infrastructure/${resource}`, {
          params: category ? { campus_id: campusId, category } : { campus_id: campusId },
        })
        .then((r) => r.data),
    enabled: !!campusId && open,
  });

  const byKind = useMemo(() => {
    const m = new Map();
    for (const f of listQ.data || []) m.set(f.kind, f);
    return m;
  }, [listQ.data]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey });
    qc.invalidateQueries({ queryKey: ["infra-dashboard", campusId] });
  };

  const wrap = async (fn) => {
    setStatus("saving");
    try {
      await fn();
      setStatus("saved");
      invalidate();
      setTimeout(() => setStatus("idle"), 1200);
    } catch (err) {
      setStatus("error");
      toast.error(err?.response?.data?.detail || "Something went wrong");
    }
  };

  const enable = (spec) =>
    wrap(async () => {
      const payload = { campus_id: campusId, kind: spec.kind, name: spec.label, config: {} };
      if (category) payload.category = category;
      await api.post(`/infrastructure/${resource}`, payload);
      setExpanded((prev) => new Set(prev).add(spec.kind));
    });
  const disable = (spec) => {
    const doc = byKind.get(spec.kind);
    if (!doc) return;
    return wrap(async () => {
      await api.delete(`/infrastructure/${resource}/${doc.id}`);
      setExpanded((prev) => {
        const n = new Set(prev);
        n.delete(spec.kind);
        return n;
      });
    });
  };
  const toggle = (kind) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(kind) ? n.delete(kind) : n.add(kind);
      return n;
    });

  const searchQ = q.trim().toLowerCase();
  const filtered = (catalog || []).filter((s) => !searchQ || s.label.toLowerCase().includes(searchQ));
  const enabledCount = (catalog || []).filter((c) => byKind.has(c.kind)).length;
  const key = testidKey || category || resource;

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={title}
      subtitle={`${subtitle} · ${enabledCount} of ${catalog.length} enabled`}
      right={<SaveStatus status={status} />}
      width="max-w-4xl"
      testid={`facilities-${key}`}
    >
      <div className="p-6 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${title.toLowerCase()}…`}
            className="pl-9"
            data-testid={`fac-search-${key}`}
          />
        </div>

        {listQ.isLoading ? (
          <div className="py-12 flex items-center justify-center text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <ul className="space-y-2" data-testid={`fac-list-${key}`}>
            {filtered.map((spec) => {
              const doc = byKind.get(spec.kind);
              const isEnabled = !!doc;
              const isOpen = expanded.has(spec.kind);
              return (
                <li key={spec.kind} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                  <div className="px-4 py-3 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => isEnabled && toggle(spec.kind)}
                      className={cn("text-slate-400 shrink-0", !isEnabled && "opacity-40 cursor-not-allowed")}
                      data-testid={`fac-toggle-${spec.kind}`}
                    >
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className={cn("text-sm font-semibold", isEnabled ? "text-slate-900" : "text-slate-500")}>
                        {spec.label}
                      </div>
                      {isEnabled && doc.config && Object.keys(doc.config).length > 0 && (
                        <div className="text-xs text-slate-500 truncate">
                          {summariseConfig(doc.config)}
                        </div>
                      )}
                    </div>
                    <EnableSwitch
                      enabled={isEnabled}
                      onChange={() => (isEnabled ? disable(spec) : enable(spec))}
                      testid={`fac-switch-${spec.kind}`}
                    />
                  </div>
                  {isEnabled && isOpen && (
                    <FacilityForm
                      doc={doc}
                      spec={spec}
                      resource={resource}
                      campusId={campusId}
                      onSaved={invalidate}
                      setStatus={setStatus}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </SlideOver>
  );
}

function summariseConfig(config) {
  return Object.entries(config)
    .filter(([, v]) => v !== "" && v !== null && v !== undefined && v !== false)
    .slice(0, 3)
    .map(([k, v]) => `${k.replace(/_/g, " ")}: ${typeof v === "boolean" ? "yes" : v}`)
    .join(" · ");
}

function EnableSwitch({ enabled, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
        enabled ? "bg-emerald-500" : "bg-slate-200"
      )}
      role="switch"
      aria-checked={enabled}
      data-testid={testid}
    >
      <span
        className={cn(
          "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
          enabled ? "translate-x-6" : "translate-x-1"
        )}
      />
    </button>
  );
}

function FacilityForm({ doc, spec, resource = "facilities", campusId, onSaved, setStatus }) {
  const [form, setForm] = useState(doc.config || {});

  useEffect(() => {
    setForm(doc.config || {});
  }, [doc.id]);

  const commit = async (key, val) => {
    if (form[key] === val && (doc.config || {})[key] === val) return;
    setStatus("saving");
    try {
      const nextConfig = { ...(doc.config || {}), [key]: val };
      await api.patch(`/infrastructure/${resource}/${doc.id}`, { config: nextConfig });
      setStatus("saved");
      onSaved();
      setTimeout(() => setStatus("idle"), 1200);
    } catch (err) {
      setStatus("error");
      toast.error("Save failed");
    }
  };

  const patchField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStatus("saving");
    try {
      const { file_id } = await InfraAPI.upload(file, campusId);
      const nextPhotos = [...(form.photo_file_ids || []), file_id];
      await api.patch(`/infrastructure/${resource}/${doc.id}`, { config: { ...(doc.config || {}), photo_file_ids: nextPhotos } });
      setForm((f) => ({ ...f, photo_file_ids: nextPhotos }));
      setStatus("saved");
      onSaved();
      toast.success("Photo added");
    } catch (err) {
      setStatus("error");
      toast.error("Upload failed");
    }
  };

  const removePhoto = async (id) => {
    setStatus("saving");
    try {
      const nextPhotos = (form.photo_file_ids || []).filter((x) => x !== id);
      await api.patch(`/infrastructure/${resource}/${doc.id}`, { config: { ...(doc.config || {}), photo_file_ids: nextPhotos } });
      setForm((f) => ({ ...f, photo_file_ids: nextPhotos }));
      setStatus("saved");
      onSaved();
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(spec.fields || []).map((f) => (
          <FieldRenderer
            key={f.key}
            field={f}
            value={form[f.key]}
            onChange={(v) => patchField(f.key, v)}
            onCommit={(v) => commit(f.key, v)}
            testidPrefix={`fac-${spec.kind}`}
          />
        ))}
      </div>

      <div className="mt-4">
        <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Photos</Label>
        <div className="flex items-center gap-2 flex-wrap">
          {(form.photo_file_ids || []).map((id) => (
            <div key={id} className="relative group">
              <img src={InfraAPI.fileUrl(id)} alt="" className="h-14 w-14 rounded-lg object-cover border border-slate-200" />
              <button
                type="button"
                onClick={() => removePhoto(id)}
                className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-white shadow border border-slate-200 opacity-0 group-hover:opacity-100 flex items-center justify-center"
                data-testid={`fac-${spec.kind}-photo-del`}
              >
                <Trash2 className="h-3 w-3 text-rose-500" />
              </button>
            </div>
          ))}
          <label className="h-14 w-14 rounded-lg border-2 border-dashed border-slate-200 hover:border-slate-300 flex items-center justify-center cursor-pointer">
            <Upload className="h-4 w-4 text-slate-400" />
            <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid={`fac-${spec.kind}-photo-upload`} />
          </label>
        </div>
      </div>
    </div>
  );
}

function FieldRenderer({ field, value, onChange, onCommit, testidPrefix }) {
  const testid = `${testidPrefix}-${field.key}`;
  const label = <Label className="text-xs font-medium text-slate-600 mb-1.5 block">{field.label}</Label>;

  if (field.type === "text" || field.type === "number") {
    return (
      <div className={field.type === "textarea" ? "sm:col-span-2" : ""}>
        {label}
        <Input
          type={field.type === "number" ? "number" : "text"}
          value={value ?? ""}
          placeholder={field.placeholder}
          onChange={(e) => onChange(field.type === "number" ? (e.target.value ? Number(e.target.value) : null) : e.target.value)}
          onBlur={(e) => onCommit(field.type === "number" ? (e.target.value ? Number(e.target.value) : null) : e.target.value)}
          data-testid={testid}
        />
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className="sm:col-span-2">
        {label}
        <Textarea
          rows={2}
          value={value ?? ""}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onCommit(e.target.value)}
          data-testid={testid}
        />
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div>
        {label}
        <Select
          value={value ?? ""}
          onValueChange={(v) => {
            onChange(v);
            onCommit(v);
          }}
        >
          <SelectTrigger data-testid={testid}><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {(field.options || []).map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (field.type === "checkbox") {
    return (
      <div className="flex items-center justify-between rounded-lg bg-white border border-slate-200 px-3 py-2">
        <span className="text-sm text-slate-700">{field.label}</span>
        <EnableSwitch
          enabled={!!value}
          onChange={() => {
            const next = !value;
            onChange(next);
            onCommit(next);
          }}
          testid={testid}
        />
      </div>
    );
  }
  return null;
}
